import {Autocomplete, Field, FieldsContainer, globalMessageHandler, TextField} from "../../../common";
import {useEffect, useState} from "react";
import I18n from "i18n-js";
import {
  Button,
  Card,
  CardContent,
  CardHeader, FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography
} from "@mui/material";
import {Item} from "../../../components/FormEditor/Item";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CardActions from "@mui/material/CardActions";
import IFramePreview from "../../../components/IFrame/IFramePreview";
import {HexColorPicker} from "react-colorful";
import ColorPicker from "../../../components/ColorPicker/ColorPicker";

export interface WebButtonProps {
  sharingUrl: string;
}

export default function WebButton({sharingUrl}: WebButtonProps) {
  const [buttonText, setButtonText] = useState<string>(I18n.t("Sharing.tools.webButton.defaultButtonText"));
  const [code, setCode] = useState<string>("");
  const [fontSize, setFontSize] = useState<number>(20);
  const [textColor, setTextColor] = useState<string>('#FFFFFF');
  const [backgroundColor, setBackgroundColor] = useState<string>('#FF9800');
  const fontSizes = [
    {label: I18n.t("Sharing.tools.webButton.fontSize.large"), value: 20},
    {label: I18n.t("Sharing.tools.webButton.fontSize.medium"), value: 15},
    {label: I18n.t("Sharing.tools.webButton.fontSize.small"), value: 10},
  ];

  useEffect(() => {
    setCode(`
      <a href="${sharingUrl}" target="_blank">
        <div class="signwaivers_button"
          style="
            display: inline-block !important;
            cursor: pointer !important;
            padding: 5px 20px !important;
            border-radius: 7px !important;
            text-align: center !important;
            font-size: ${fontSize}px !important;
            background-color: ${backgroundColor} !important;
            color: ${textColor} !important;
            font-family: sans-serif !important;
          ">
          ${buttonText}
        </div>
      </a>`
      // Remove formatting so it will appear not as code format
      .replace(/\n/g, ' ')
      .replace(/\s\s+/g, ' ')
      .trim());
  }, [buttonText, fontSize, textColor, backgroundColor]);

  return (
    <Card>
      <CardHeader title={I18n.t("Sharing.tools.webButton.title")}/>
      <CardContent>
        <Grid container>
          <Grid item md={6} sm={12} sx={{paddingRight: {md: '5px', sm: '0px'}}}>
            <Typography variant="subtitle2" sx={{marginBottom: "10px"}}>
              {I18n.t("Sharing.tools.webButton.customizeWebButton")}
            </Typography>
            <FieldsContainer>
              <Field fullWidth>
                <TextField
                  label={I18n.t("Sharing.tools.webButton.attributes.buttonText")}
                  value={buttonText}
                  useDelayedKeystrokes={false}
                  onChange={(evt: any) => setButtonText(evt.target.value)}
                />
              </Field>
              <Field fullWidth>
                <Autocomplete
                  label={I18n.t("Sharing.tools.webButton.attributes.fontSize")}
                  value={fontSize}
                  options={fontSizes}
                  onChange={(evt: any) => setFontSize(evt.target.value)}
                />
              </Field>
              <Field half sx={{flex: 1, alignItems: "center", justifyItems: "center"}}>
                <ColorPicker
                  label={I18n.t("Sharing.tools.webButton.attributes.backgroundColor")}
                  value={backgroundColor}
                  setValue={setBackgroundColor}
                />
              </Field>
              <Field half sx={{flex: 1, alignItems: "center", justifyItems: "center"}}>
                <ColorPicker
                  label={I18n.t("Sharing.tools.webButton.attributes.textColor")}
                  value={textColor}
                  setValue={setTextColor}
                />
              </Field>
            </FieldsContainer>
          </Grid>
          <Grid item md={6} sm={12} sx={{paddingLeft: {md: '5px', sm: '0px'}}}>
            <Typography variant="subtitle2" sx={{marginBottom: "10px"}}>
              {I18n.t("Sharing.tools.webButton.preview")}
            </Typography>
            <IFramePreview
              code={code}
            />
            <FieldsContainer>
              <Field fullWidth>
                <TextField
                  label={I18n.t("Sharing.tools.webButton.attributes.code")}
                  value={code}
                  fullWidth
                  multiline
                  editable={false}
                />
              </Field>
            </FieldsContainer>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Button
          startIcon={<ContentCopyIcon/>}
          onClick={() => {
            navigator.clipboard.writeText(code);
            globalMessageHandler.success(I18n.t("App.codeCopied"));
          }}
        >
          {I18n.t("Sharing.tools.webButton.copyButton")}
        </Button>
      </CardActions>
    </Card>
  );
}